import EventEmitter from "./EventEmitter.mts";
import UFunction from "./UFunction.mts";

declare namespace Channel {
  export type Message<
    Name extends string = string,
    Data extends any = any,
    Result extends any = any,
  > = [name: Name, data: Data, result?: Result];

  export type Definition<
    Send extends Message[] = Message[],
    Receive extends Message[] = Message[],
  > = {
    send: Send;
    receive: Receive;
  };

  export namespace Definition {
    export type Reverse<Definition extends Channel.Definition | Channel<any>> =
      (
        Definition extends Channel<infer D> ? D : Definition
      ) extends infer D extends Channel.Definition
        ? {
            send: D["receive"];
            receive: D["send"];
          }
        : never;
  }

  export type Port<Definition extends Channel.Definition<any[], any[]>> = {
    send<Name extends Definition["send"][number][0]>(
      name: Name,
      data: Extract<Definition["send"][number], Channel.Message<Name>>[1]
    ): Extract<Definition["send"][number], Channel.Message<Name>>[2];
    receive(
      receive: (
        name: Definition["receive"][number][0],
        data: Definition["receive"][number][1]
      ) => void
    ): void;
  };
}

class Channel<Definition extends Channel.Definition> {
  private events = new EventEmitter<Definition["receive"]>();

  constructor(private readonly port: Channel.Port<Definition>) {}

  private init = UFunction.memo(() => {
    this.port.receive((name, data) => {
      this.events.emit(name, data);
    });
  });

  send<Name extends Definition["send"][number][0]>(
    name: Name,
    ...[data]: Extract<
      Definition["send"][number],
      Channel.Message<Name>
    >[1] extends infer Data
      ? Data extends undefined
        ? [undefined?]
        : [Data]
      : never
  ) {
    return this.port.send(name, data);
  }

  receive<Name extends Definition["receive"][number][0]>(
    name: Name,
    receive: (
      data: Extract<Definition["receive"][number], Channel.Message<Name>>[1]
    ) => void
  ) {
    this.init();
    this.events.on(name, receive);
  }
}

export default Channel;
